import type { CSSProperties } from "react";

interface Props {
  width?: string | number;
  height?: string | number;
  style?: CSSProperties;
  className?: string;
}

/**
 * Tiny shimmering placeholder block. Use it to compose screen-shaped skeletons during the
 * first load. The shimmer animation is driven by the `.sk-shimmer` keyframes in styles.css.
 */
export function Skeleton({ width = "100%", height = 12, style, className }: Props) {
  return (
    <span
      aria-hidden="true"
      className={"sk-shimmer " + (className ?? "")}
      style={{
        display: "inline-block",
        width,
        height,
        borderRadius: 4,
        background:
          "linear-gradient(90deg, var(--gw-neutral-100) 0%, var(--gw-neutral-50) 50%, var(--gw-neutral-100) 100%)",
        backgroundSize: "200% 100%",
        animation: "sk-shimmer 1.4s ease-in-out infinite",
        ...style,
      }}
    />
  );
}

interface RowSkeletonProps {
  cols?: number;
  rows?: number;
}

export function TableSkeleton({ cols = 6, rows = 8 }: RowSkeletonProps) {
  return (
    <div aria-busy="true" aria-live="polite" style={{ padding: 12 }}>
      <div style={{ display: "flex", gap: 12, marginBottom: 16 }}>
        {Array.from({ length: cols }).map((_, i) => (
          <Skeleton key={`h${i}`} width={`${100 / cols}%`} height={14} />
        ))}
      </div>
      {Array.from({ length: rows }).map((_, r) => (
        <div key={`r${r}`} style={{ display: "flex", gap: 12, marginBottom: 10 }}>
          {Array.from({ length: cols }).map((_, c) => (
            <Skeleton key={`r${r}c${c}`} width={`${100 / cols}%`} height={16} />
          ))}
        </div>
      ))}
    </div>
  );
}

export function TilesSkeleton({ count = 5 }: { count?: number }) {
  return (
    <div className="summary" aria-busy="true">
      {Array.from({ length: count }).map((_, i) => (
        <div key={i} className="tile">
          <Skeleton width="60%" height={11} style={{ marginBottom: 8 }} />
          <Skeleton width="40%" height={28} style={{ marginBottom: 6 }} />
          <Skeleton width="50%" height={11} />
        </div>
      ))}
    </div>
  );
}
